import { type ReactNode, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { mtc } from '@/styles'
import { SignupHeader } from './SignupHeader'

interface SignupPageLayoutProps {
  children: ReactNode
  /** Hide the progress bar (for terminal pages like success, disqualified, consult) */
  hideProgress?: boolean
  /** Sticky footer content (typically SignupFooter with Back/Continue buttons) */
  footer?: ReactNode
  /** 'default' = max-w-[700px], 'wide' = max-w-[756px], 'extraWide' = max-w-[900px] for plan-comparison layouts */
  maxWidth?: 'default' | 'wide' | 'extraWide'
  /** 'default' = px-14, 'reduced' = px-7, for pages that need less side margin */
  horizontalPadding?: 'default' | 'reduced'
  /** Additional classes for the main content area */
  className?: string
}

const MAX_WIDTH_MAP = {
  default: 'max-w-[700px]',
  wide: 'max-w-[756px]',
  extraWide: 'max-w-[900px]',
} as const

const HORIZONTAL_PADDING_MAP = {
  default: 'px-14',
  reduced: 'px-7',
} as const

export const SignupPageLayout = ({
  children,
  hideProgress,
  footer,
  maxWidth = 'default',
  horizontalPadding = 'default',
  className,
}: SignupPageLayoutProps) => {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  // Header, content, and footer are all constrained to the same max-width
  // and centered with mx-auto so every signup page lines up vertically:
  // the stepper, the page body, and the back/continue buttons share the
  // same left and right edges.
  const innerClasses = mtc('w-full mx-auto', MAX_WIDTH_MAP[maxWidth])
  const paddingClasses = HORIZONTAL_PADDING_MAP[horizontalPadding]

  return (
    <div className={mtc('flex flex-col min-h-screen py-8', paddingClasses)}>
      {/* Header: logo, logout, progress bar */}
      <div className={innerClasses}>
        <SignupHeader hideProgress={hideProgress} />
      </div>

      {/* Main content area — grows to fill space, constrained width, centered */}
      <div
        className={mtc(
          'flex-1 mt-8',
          innerClasses,
          footer ? 'pb-20' : '',
          className
        )}
      >
        {children}
      </div>

      {/* Fixed footer with border and buttons. Outer div spans the viewport
          for the border + background; inner div constrains the buttons to
          match the content above. */}
      {footer && (
        <div
          className={mtc(
            'fixed bottom-0 left-0 right-0 bg-neutral-0 border-t border-neutral-100 pt-4 pb-8 z-50',
            paddingClasses
          )}
        >
          <div className={innerClasses}>{footer}</div>
        </div>
      )}
    </div>
  )
}
